"use client";

import { Suspense, useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { destinationSure } from "@/lib/callback-url";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

function LoginForm() {
  const t = useTranslations("auth");
  const c = useTranslations("common");
  const router = useRouter();
  const params = useSearchParams();
  // Destination de retour : une invitation à un siège de licence renvoie ici
  // puis veut qu'on revienne sur son lien. Filtrée — voir `destinationSure`.
  const retour = destinationSure(params.get("callbackUrl"));
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await signIn("credentials", {
      email,
      password,
      redirect: false,
    });

    setLoading(false);

    if (result?.error) {
      setError(t("invalidCredentials"));
    } else {
      router.push(retour);
      router.refresh();
    }
  }

  return (
    <Card className="mx-auto max-w-md border-border bg-[var(--bg-secondary)]">
      <CardHeader>
        <CardTitle className="text-foreground">{t("login")}</CardTitle>
        <CardDescription>
          {t("loginSubtitle")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-md bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">{t("email")}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t("emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="border-border bg-[var(--bg-tertiary)]"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">{t("password")}</Label>
              <Link
                href="/forgot-password"
                className="text-xs text-[var(--accent-primary)] hover:underline"
              >
                {t("forgotPassword")}
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="border-border bg-[var(--bg-tertiary)]"
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? t("loginLoading") : t("loginAction")}
          </Button>
        </form>

      </CardContent>
      <CardFooter className="justify-center">
        <p className="text-sm text-muted-foreground">
          {t("noAccount")}{" "}
          <Link
            href="/register"
            className="text-[var(--accent-primary)] hover:underline"
          >
            {t("createAccount")}
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}

/**
 * `useSearchParams` exige une frontière Suspense en App Router : sans elle, le
 * rendu statique de la page échoue au build. Même motif que la page de
 * réinitialisation de mot de passe.
 */
export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginForm />
    </Suspense>
  );
}
